import { MovieClip, View, getRelativeOffset } from '@play-co/timestep-core/ui';
import { Container, randomRange } from '../../../odie';
import { IEffect } from '../../actions/effects/IEffect';
import { app } from '../../app';
import {
  despawnOverlayEntity,
  spawnOverlayEntity,
} from '../../entities/OverlayEntity';
import { GameScene } from '../../GameScene';
import { getMatch2AssetPath } from '../../util/assets';
import { blockViewAssets } from '../../util/blockTools';
import { BigEggBlockEntity } from './BigEggBlockEntity';
import { config } from '../../defs/config';
import gsap from 'gsap';
import { getGoals } from 'src/game/scenes/Match2Scene';

export class BigEggBlockEffect implements IEffect {
  private readonly scene: GameScene;

  constructor(scene: GameScene) {
    this.scene = scene;
  }

  public static assets() {
    return blockViewAssets('bigEgg');
  }

  public async execute() {
    //
  }

  public async animateBreak(
    entity: BigEggBlockEntity,
    strength: 'strong' | 'soft',
  ) {
    const textures: string[] = [
      getMatch2AssetPath('blocks/bigEgg/15.png'),
      getMatch2AssetPath('blocks/bigEgg/17.png'),
      getMatch2AssetPath('blocks/bigEgg/19.png'),
    ];
    const strong = strength === 'strong';
    const scale = strong ? 1 : 0.6;

    const particles = app.particles.create({
      textures,
      rate: 0,
      limit: Math.floor(20 * scale),
      emitterDuration: 0,
      duration: 1,
      velocity: { x: 0, y: -200 },
      scale: [
        { x: 1.4 * scale, y: 1.4 * scale },
        { x: 2.1 * scale, y: 2.1 * scale },
      ],
      behaviors: [
        {
          type: 'emiterShape',
          shape: 'circle',
          radius: config.tile.size / 2,
        },
        { type: 'explode', magnitude: [200 * scale, 300 * scale] },
        { type: 'gravity', gravity: { x: 0, y: 1000 } },
        { type: 'scale', to: 0 },
        { type: 'kinematic' },
      ],
    });

    const position = entity.c.position.mapPosition.clone();
    const overlay = spawnOverlayEntity(
      this.scene,
      position,
      entity.c.block.props,
      () => particles.view,
      'foreground',
    );
    await particles.start();
    despawnOverlayEntity(this.scene, overlay);
  }

  public async animateChickFly(entity: BigEggBlockEntity) {
    const movieclip = new MovieClip({
      url: getMatch2AssetPath('blocks/bigEgg'),
      defaultAnimation: 'chick_fly',
    });
    movieclip.play('chick_fly', null, true);
    await this.flyToGoals(entity.c.view2d.view.view, movieclip);
    movieclip.removeFromSuperview();
  }

  public async animateShake(
    entity: BigEggBlockEntity,
    strength: 'strong' | 'soft',
  ) {
    const view = entity.c.blockBigEgg.view.movieclip;
    const duration = 0.5;
    const magnitude = { value: strength === 'strong' ? 10 : 7 };
    gsap.killTweensOf(view);
    await gsap.to(magnitude, {
      value: 0,
      duration,
      ease: 'linear',
      onUpdate: () => {
        view.style.x = randomRange(-magnitude.value, magnitude.value);
        view.style.y = randomRange(-magnitude.value, magnitude.value);
      },
      onComplete: () => {
        view.style.x = 0;
        view.style.y = 0;
      },
    });
  }

  private async flyToGoals(from: View, chick: View) {
    const duration = randomRange(0.5, 0.7);
    const goals = getGoals();
    const target = goals?.getGoal('bigEgg');

    // Goals and target goal are required to this animation
    if (!goals || !target) return;

    const goalsOffset = getRelativeOffset(from, goals);
    const targetOffset = getRelativeOffset(target, goals);

    const fromX = chick.style.x + goalsOffset.x + config.tile.size;
    const fromY = chick.style.y + goalsOffset.y + config.tile.size;
    const toX = targetOffset.x + target.style.width * 0.5;
    const toY = targetOffset.y + target.style.height * 0.5;

    chick.style.x = fromX;
    chick.style.y = fromY;

    goals.addSubview(chick);

    const scaleUp = { scale: 3 };
    const scaleToGoal = { scale: 1 };

    gsap.to(chick.style, {
      ...scaleUp,
      duration: duration * 0.3,
      ease: 'sine.out',
    });

    gsap.to(chick.style, {
      ...scaleToGoal,
      duration: duration * 0.7,
      ease: 'sine.in',
      delay: duration * 0.3,
    });

    await gsap.to(chick.style, {
      x: toX,
      y: toY,
      duration,
      ease: 'sine.inOut',
    });
  }
}
